<template>
  <div class="live-stage-map">
    <map-view
      ref="map"
      class="live-stage-map__map"
      :map-bounds="mapBounds"
      :map-center="center"
      @click="$emit('click', $event)"
    >
      <o-map-polyline
        v-for="route in filteredRoutes"
        :key="'route-' + route.id"
        :name="route.name"
        :path="route.path"
        :color="route.color"
        :opacity="routeOpacity"
        :dashed="true"
        :strokeWeight="4"
        :show-direction="true"
        @click="$emit('selectRoute', $event, route)"
      />

      <o-map-zone
        v-for="zone in stageSpeedZones"
        :key="zone.id"
        :name="zone.name"
        :path="zone.path"
        :color="zone.color"
        :radius="zone.distance"
        @click="$emit('selectZone', $event, zone)"
      />

      <o-map-square-marker
        v-for="waypoint in filteredWaypoints"
        :key="waypoint.id"
        :label="waypoint.name"
        :iconName="waypoint.icon"
        :color="waypoint.color"
        :lat-lng="waypoint.location"
        :opacity="waypointOpacity"
      />

      <o-map-zone-circle-marker
        v-for="checkpoint in checkpoints"
        :key="`checkpoint-${checkpoint.id}`"
        :color="checkpoint.color"
        :label="checkpoint.name"
        :radius="checkpoint.radius"
        :lat-lng="checkpoint.location"
      />

      <o-map-square-marker
        v-for="geofence in geofences"
        :key="geofence.id"
        :label="geofence.label"
        iconName="circle"
        :color="geofence.color"
        :lat-lng="geofence.location"
        @click="$emit('selectGeofence', geofence)"
      />

      <o-map-zone-circle-marker
        v-for="geofence in geofences"
        :key="`geofence-${geofence.id}`"
        :color="geofence.color"
        :label="geofence.label"
        :radius="geofence.radius"
        :lat-lng="geofence.location"
        :tooltip-options="{ offset: [0, -8] }"
      />

      <slot />
    </map-view>
    <b-field class="live-stage-map__radio">
      <b-radio-button
        v-for="mapRadioButton in mapRadioButtons"
        :key="mapRadioButton.nativeValue"
        v-model="fields.accessType"
        :native-value="mapRadioButton.nativeValue"
      >
        <span>{{ mapRadioButton.label }}</span>
      </b-radio-button>
    </b-field>
  </div>
</template>

<script>
import MapView from '@views/MapView/index.vue'
import OMapZone from '@components/Map/MapZone.vue'
import OMapPolyline from '@components/Map/MapPolyline'
import OMapSquareMarker from '@components/Map/MapSquareMarker'
import OMapZoneCircleMarker from '@components/Map/MapZoneCircleMarker'
import { STAGE_ACCESS_TYPE_PUBLIC, STAGE_ACCESS_TYPE_PRIVATE } from '@constants/stage/accessType'
import { WAYPOINT_ACCESS_TYPE_PUBLIC } from '@constants/waypoint/accessType'
import { TRACKER_INTERVAL_COLORS, TRACKER_INTERVAL_LABELS } from '@constants/tracker/interval'

export default {
  name: 'LiveStageMap',

  components: {
    OMapZone,
    MapView,
    OMapPolyline,
    OMapSquareMarker,
    OMapZoneCircleMarker,
  },

  props: {
    stageRoutes: {
      type: Object,
      default: () => ({ [STAGE_ACCESS_TYPE_PUBLIC]: [], [STAGE_ACCESS_TYPE_PRIVATE]: [] }),
      validator: (v) =>
        [STAGE_ACCESS_TYPE_PUBLIC, STAGE_ACCESS_TYPE_PRIVATE].every((key) => key in v),
    },
    routeOpacity: {
      type: Number,
      default: 1,
    },
    stageWaypoints: {
      type: Array,
      default: () => [],
    },
    waypointOpacity: {
      type: Number,
      default: 1,
    },
    stageSpeedZones: {
      type: Array,
      default: () => [],
    },
    trackerGeofences: {
      type: Array,
      default: () => [],
    },
    center: {
      type: Array,
      default: () => [0, 0],
    },
    bounds: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      fields: {
        accessType: STAGE_ACCESS_TYPE_PRIVATE,
      },
    }
  },

  created() {
    this.mapRadioButtons = [
      { nativeValue: STAGE_ACCESS_TYPE_PUBLIC, label: 'Public' },
      { nativeValue: STAGE_ACCESS_TYPE_PRIVATE, label: 'Privé' },
    ]
  },

  computed: {
    filteredRoutes() {
      if (
        this.stageRoutes[STAGE_ACCESS_TYPE_PRIVATE].length === 0 &&
        this.stageRoutes[STAGE_ACCESS_TYPE_PUBLIC].length > 0
      ) {
        return this.stageRoutes[STAGE_ACCESS_TYPE_PUBLIC]
      }

      return this.stageRoutes[this.fields.accessType]
    },

    filteredWaypoints() {
      return this.fields.accessType === WAYPOINT_ACCESS_TYPE_PUBLIC
        ? this.stageWaypoints.filter((waypoint) => waypoint.accessType === this.fields.accessType)
        : this.stageWaypoints
    },

    checkpoints() {
      return this.filteredWaypoints.filter((waypoint) => waypoint.radius)
    },

    geofences() {
      return this.trackerGeofences.map((geofence) => ({
        ...geofence,
        color: TRACKER_INTERVAL_COLORS[geofence.inTrackerInterval],
        label: `Geofence : ${geofence.name}\nIntervalle d'entrée : ${
          TRACKER_INTERVAL_LABELS[geofence.inTrackerInterval]
        }`,
      }))
    },

    mapBounds() {
      if (this.bounds) {
        return this.bounds
      }

      if (this.filteredRoutes.length) {
        return this.filteredRoutes[0].bounds
      }

      const locations = [
        ...this.filteredWaypoints.map((waypoint) => waypoint.location),
        ...this.trackerGeofences.map((geofence) => geofence.location),
      ]

      if (locations.length === 0) {
        return null
      }

      return this.$refs.map.computeBounds(locations)
    },
  },

  methods: {
    getBounds() {
      return this.$refs.map.getBounds()
    },

    getCenter() {
      return this.$refs.map.getCenter()
    },
  },
}
</script>

<style lang="scss">
@import 'variables';

$map-view-min-height: 50vh;

.live-stage-map {
  overflow: hidden;

  &__map {
    height: 100%;
    min-height: $map-view-min-height;
  }

  &__radio {
    position: absolute;
    top: $spacing-s;
    left: $spacing-s;
    z-index: 1;
  }
}
</style>
